import { showToast } from '../../../engine/toast';
import { adminGetCompany, adminSaveCompany, adminGetMeta, adminSaveMeta } from '../admin-api';
import { escapeHtml, BTN_CLS, INPUT_CLS } from '../admin-ui';
import type { PaneCtx, PaneModule } from './types';

/**
 * The company and meta panes: two instances of the same flat key/value form.
 *
 * Both field lists are declared explicitly rather than rendered from whatever
 * the server returns, so a new key appearing upstream cannot silently become
 * an unlabelled input.
 */
const COMPANY_FIELDS: { key: string; label: string }[] = [
  { key: 'name', label: 'Legal name' },
  { key: 'game_name', label: 'Game name' },
  { key: 'email', label: 'Support email' },
  { key: 'address', label: 'Address' },
  { key: 'registration', label: 'Registration number' },
  { key: 'vat', label: 'VAT number' },
  { key: 'jurisdiction', label: 'Jurisdiction' },
  { key: 'domain', label: 'Domain' },
];

const META_FIELDS: { key: string; label: string }[] = [
  { key: 'title', label: 'Title' },
  { key: 'description', label: 'Description' },
  { key: 'site_name', label: 'Site name' },
  { key: 'url', label: 'Canonical URL' },
  { key: 'locale', label: 'Locale' },
  { key: 'image', label: 'Share image URL' },
  { key: 'image_alt', label: 'Share image alt text' },
];

function fieldsFormHtml(
  formName: string,
  title: string,
  fields: { key: string; label: string }[],
  values: Record<string, string>,
  note: string,
  busy: boolean,
): string {
  return `
    <section class="flex flex-col gap-2">
      <h2 class="font-heading text-xs text-gold tracking-wide uppercase">${title}</h2>
      <form data-form="${formName}" class="glass-panel px-3 py-3 flex flex-col gap-2">
        ${fields
          .map(
            (f) => `
            <label class="flex flex-col gap-1">
              <span class="text-[10px] text-text-dim">${f.label}</span>
              <input name="${f.key}" type="text" value="${escapeHtml(values[f.key] ?? '')}" class="${INPUT_CLS}" />
            </label>`,
          )
          .join('')}
        <p class="text-[10px] text-text-dim leading-relaxed">${note}</p>
        <button type="submit" ${busy ? 'disabled' : ''} class="${BTN_CLS} self-start">Save</button>
      </form>
    </section>`;
}

function readFields(form: HTMLFormElement, fields: { key: string }[]): Record<string, string> {
  const data = new FormData(form);
  return Object.fromEntries(fields.map((f) => [f.key, String(data.get(f.key) ?? '')]));
}

export const companyPane: PaneModule = {
  id: 'company',
  label: 'Company',
  html: (ctx: PaneCtx) =>
    fieldsFormHtml(
      'company',
      'Company',
      COMPANY_FIELDS,
      ctx.state.company,
      'These values print on every invoice and sign every transactional email.',
      ctx.state.busy,
    ),
  async load(ctx) {
    ctx.state.company = (await adminGetCompany()) ?? {};
  },
  onSubmit(kind, form, ctx) {
    if (kind !== 'company') return false;
    const body = readFields(form, COMPANY_FIELDS);
    void ctx.withBusy(async () => {
      await adminSaveCompany(body);
      ctx.state.company = body;
      showToast('Company details saved.', 'success');
      ctx.repaint();
    }, 'Could not save company details.');
    return true;
  },
};

export const metaPane: PaneModule = {
  id: 'meta',
  label: 'Meta tags',
  html: (ctx: PaneCtx) =>
    fieldsFormHtml(
      'meta',
      'Meta tags',
      META_FIELDS,
      ctx.state.meta,
      'Injected into the page head server-side — crawlers do not run JS, so this is the only place they read.',
      ctx.state.busy,
    ),
  async load(ctx) {
    const res = (await adminGetMeta()) as Record<string, unknown>;
    // The endpoint answers { values, resolved, head }; the form edits the
    // stored values, not the resolved preview. `values` already carries bare
    // keys (`title`, not `meta.title`) even though the table stores them prefixed.
    const values = (res?.values ?? res) as Record<string, string>;
    ctx.state.meta = Object.fromEntries(Object.entries(values ?? {}).map(([k, v]) => [k, String(v ?? '')]));
  },
  onSubmit(kind, form, ctx) {
    if (kind !== 'meta') return false;
    const body = readFields(form, META_FIELDS);
    void ctx.withBusy(async () => {
      await adminSaveMeta(body);
      ctx.state.meta = body;
      showToast('Meta tags saved.', 'success');
      ctx.repaint();
    }, 'Could not save meta tags.');
    return true;
  },
};
